import React from "react";
import {
  Box,
  Button,
  Link,
  MagnifyingGlassIcon,
  TextInput,
  Typography
} from "@dealer-center/core-components";
import {
  CONTAINER_STYLES,
  LINK_STYLES,
  TYPOGRAPHY_STYLES
} from "../styleConstants";
import { CardIcon } from "./CardIcon";

interface ApplicationCardProps {
  title: string;
  description: string;
  icon: string;
  iconColor: string;
  url: string;
  isExternal?: boolean;
  onClick?: () => void;
  hasSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  children?: React.ReactNode;
}

const CARD_STYLES = {
  width: { xs: "100%", sm: 280 },
  minWidth: 254,
  maxWidth: "100%",
  height: 140,
  padding: "12px 16px",
  backgroundColor: "#fff",
  borderRadius: "3px",
  border: "1px solid #999",
  boxSizing: "border-box",
  cursor: "pointer",
  boxShadow: "rgba(0, 0, 0, .3) 0 0 4px 1px",
  transition: "box-shadow 0.2s",
  "&:hover": {
    boxShadow: "0 4px 8px rgba(0,0,0,0.6)"
  },
  ...CONTAINER_STYLES.flexColumn
};

export const ApplicationCard: React.FC<ApplicationCardProps> = ({
  title,
  description,
  icon,
  iconColor,
  url,
  isExternal,
  onClick,
  hasSearch,
  searchValue,
  onSearchChange,
  children
}) => {
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue?.trim()) {
      const searchObj = {
        keywords: [searchValue.trim()]
      };

      const json = JSON.stringify(searchObj);
      const encodedSearch = encodeURIComponent(json);

      const searchUrl = `${url}&search=${encodedSearch}`;

      window.open(searchUrl, "_blank");
    } else {
      window.open(url, "_self");
    }
  };
  const cardContent = (
    <>
      {/* Card Header */}
      <Box
        className="application-header"
        sx={{
          ...CONTAINER_STYLES.flexRowCenter,
          marginBottom: "12px"
        }}
      >
        <CardIcon iconColor={iconColor} iconKey={icon} title={title} />
        <Typography sx={TYPOGRAPHY_STYLES.cardHeader} variant="h6">
          {title}
        </Typography>
      </Box>

      {/* Card Description */}
      <Typography
        className="application-description"
        sx={TYPOGRAPHY_STYLES.cardDescription}
        variant="body-md"
      >
        {description}
      </Typography>

      {/* Inventory Search Form */}
      {hasSearch && (
        <Box
          className="inventory-search-block"
          component="form"
          sx={{
            ...CONTAINER_STYLES.flexRowCenter,
            gap: 1,
            marginTop: "-8px"
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          onSubmit={handleSearchSubmit}
        >
          <Box
            sx={{ flexGrow: 1 }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <TextInput
              name="inventorySearch"
              placeholder="Search inventory"
              sx={{ width: "100%" }}
              value={searchValue || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onSearchChange?.(e.target.value)
              }
            />
          </Box>
          <Button
            analyticsConfiguration={{
              eventName: "welcome_search_inventory"
            }}
            sx={{
              minWidth: "auto",
              padding: "10px",
              backgroundColor: "#1B5A8A",
              marginTop: "10px",
              "&:hover": {
                backgroundColor: "#0F3D5C"
              }
            }}
            type="submit"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
          >
            <MagnifyingGlassIcon
              style={{
                fontSize: "20px"
              }}
              title="Search inventory"
            />
          </Button>
        </Box>
      )}

      {/* Optional children (for additional custom content) */}
      {children}
    </>
  );

  // If children exists (like inventory search), render as clickable Box
  if (hasSearch || children) {
    return (
      <Box
        className="application-card inventory-card"
        sx={{
          ...CARD_STYLES,
          position: "relative"
        }}
        title={`${title}: ${description}`}
        onClick={onClick}
      >
        {/* Hidden link for accessibility */}
        <Link
          aria-label={`${title}: ${description}`}
          href={url}
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: "hidden",
            clip: "rect(0, 0, 0, 0)",
            whiteSpace: "nowrap",
            border: 0
          }}
        />
        {cardContent}
      </Box>
    );
  }

  // Regular card wrapped in Link
  return (
    <Link
      aria-label={`${title}: ${description}`}
      href={url}
      rel={isExternal ? "noopener noreferrer" : undefined}
      sx={{ ...LINK_STYLES.cardWrapper, width: { xs: "100%", sm: "auto" } }}
      target={isExternal ? "_blank" : undefined}
    >
      <Box className="application-card" sx={CARD_STYLES}>
        {cardContent}
      </Box>
    </Link>
  );
};
